import { Time } from '../../shared/types/time';
import { BrandDateFormat, HourFormat } from '../types/brand';

export const DAY_LENGTH = 24 * 60;
export const DAY_LENGTH_MILLIS = DAY_LENGTH * 60 * 1000;
const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = MS_PER_MINUTE * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;
const MS_PER_MONTH = MS_PER_DAY * 30;
const MS_PER_YEAR = MS_PER_MONTH * 12;

export const DEFAULT_HOUR_FORMAT: HourFormat = '12';
export const DEFAULT_TIMESTR_FORMAT: TimeStringFormat = 'default';

export type TimeStringFormat = 'compact' | 'locale' | 'xcompact' | 'default';

export function numpad(n: number, length: number = 2, fillChar: string = '0'): string {
  let s = String(n);
  return s.length >= length ? s : fillChar.repeat(length - s.length) + s;
}

export function timeToMinutes(time: Time, timeOffset?: number): number {
  timeOffset = timeOffset || 0;
  let minutes = normalizeMinutes(time.hour * 60 + time.minute - timeOffset);
  return minutes % DAY_LENGTH;
}

/**
 * Compares two times and returns:
 *  - 0 if they are the same
 *  - < 0 if time1 is less than time2
 *  - > 0 if time1 is greater than time2
 *
 * It optionally takes a time offset, so for example if the offset is 6 hours, then 5:00 is greater
 * than 6:00
 */
export function timeCompare(time1: Time, time2: Time, timeOffset?: number): number {
  let timeMinutes1 = timeToMinutes(time1, timeOffset);
  let timeMinutes2 = timeToMinutes(time2, timeOffset);

  return timeMinutes1 - timeMinutes2;
}

/**
 * Checks if two time ranges overlap.
 * Durations of 0/undefined are treated as discrete 1-minute points.
 */
export function doTimeRangesOverlap(
  start1: Time,
  duration1: number | undefined,
  start2: Time,
  duration2: number | undefined
): boolean {
  const start1Minutes = timeToMinutes(start1);
  const start2Minutes = timeToMinutes(start2);

  const dur1 = duration1 && duration1 > 0 ? duration1 : 1;
  const dur2 = duration2 && duration2 > 0 ? duration2 : 1;

  // Any interval that spans a full day overlaps with every non-empty interval.
  if (dur1 >= DAY_LENGTH || dur2 >= DAY_LENGTH) {
    return true;
  }

  const endOffset1 = calculateTimeOffset(start1, dur1);
  const endOffset2 = calculateTimeOffset(start2, dur2);

  const end1 = timeToMinutes(endOffset1.time) + endOffset1.dayOffset * DAY_LENGTH;
  const end2 = timeToMinutes(endOffset2.time) + endOffset2.dayOffset * DAY_LENGTH;

  const overlaps = (aStart: number, aEnd: number, bStart: number, bEnd: number) =>
    aStart < bEnd && bStart < aEnd;

  // Compare the second interval in previous/current/next-day positions to handle wrap-around.
  return (
    overlaps(start1Minutes, end1, start2Minutes - DAY_LENGTH, end2 - DAY_LENGTH) ||
    overlaps(start1Minutes, end1, start2Minutes, end2) ||
    overlaps(start1Minutes, end1, start2Minutes + DAY_LENGTH, end2 + DAY_LENGTH)
  );
}

// WARNING: The format and hour24 parameters have been switched from the SET version!!!
export function timeToString(
  time: Time,
  hourFormat: HourFormat,
  timeFormat: TimeStringFormat
): string {
  // ensure the time is not over 24 h, 0 mins
  time.hour = time.hour % 24;
  time.minute = time.minute % 60;

  if (hourFormat === '24') {
    return timeToString24(time, timeFormat);
  }

  let t = Object.assign({}, time);
  let suffix;

  let am;
  let pm;

  switch (timeFormat) {
    case 'compact':
    case 'xcompact':
      am = '';
      pm = 'p';
      break;
    case 'locale':
      am = ' AM';
      pm = ' PM';
      break;
    case 'default':
    default:
      am = 'am';
      pm = 'pm';
      break;
  }

  if (t.hour === 0) {
    t.hour = 12;
    suffix = am;
  } else if (t.hour === 12) {
    suffix = pm;
  } else {
    suffix = t.hour < 12 ? am : pm;
    t.hour %= 12;
  }

  return timefmt(t, timeFormat) + suffix;
}

// WARNING: The negative clamping is not consistent with SET!!! (i.e.,
// `minutesToTime(-1)` -> {h: -1, minute: -1} in SET, but {h: 23, m: 59} in GO)
export function minutesToTime(minutes: number): Time {
  minutes = normalizeMinutes(minutes) % DAY_LENGTH;

  return {
    hour: Math.floor(minutes / 60),
    minute: minutes % 60,
  };
}

export function minutesToTimeString(
  minutes: number,
  hourFormat: HourFormat,
  timeFormat: TimeStringFormat
): string {
  return timeToString(minutesToTime(minutes), hourFormat, timeFormat);
}

export function addMinutesToTime(time: Time, minutes: number): Time {
  let timeMinutes = timeToMinutes(time);
  return minutesToTime(timeMinutes + minutes);
}

export function minutesSinceDayStart(minutes: number, dayStartMinutes: number) {
  return (minutes - dayStartMinutes + DAY_LENGTH) % DAY_LENGTH;
}

/**
 * Adds a number of minutes to `startTime`. The result is an object with the new
 * time and a number of days to offset, in case the time goes to the next day.
 */
export function calculateTimeOffset(startTime: Time, minutes: number, dayStart?: number) {
  dayStart = dayStart || 0;

  let time = addMinutesToTime(startTime, minutes);
  let minutesOffset = minutesSinceDayStart(timeToMinutes(startTime), dayStart);
  let dayOffset = Math.floor((minutesOffset + minutes) / DAY_LENGTH);

  return {
    time,
    dayOffset,
  };
}

export function calculateTimeDifference(endTime: Time, startTime: Time): number {
  let endTimeMinutes = timeToMinutes(endTime);
  let startTimeMinutes = timeToMinutes(startTime);

  return normalizeMinutes(endTimeMinutes - startTimeMinutes);
}

export function staffMinutesToTime(mins: number, startTime: Time, hourFormat: HourFormat) {
  let startTimeMinutes = timeToMinutes(startTime);
  let time = normalizeMinutes(mins + startTimeMinutes) % DAY_LENGTH;
  let hourStr = Math.trunc(time / 60);
  let minStr = Math.abs(time % 60);

  let timeString =
    hourFormat === '12'
      ? timeToString({ hour: hourStr, minute: minStr }, hourFormat, 'default')
      : `${staffTimePad(hourStr)}:${staffTimePad(minStr)}`;

  // Remove the leading zero, if any
  if (hourFormat === '12' && timeString.startsWith('0')) {
    let hour = timeString.split(':')[0];
    if (hour.length === 2) {
      timeString = timeString.slice(1);
    }
  }

  return timeString;
}

export function staffMinutesToRelativeTime(mins: number): string {
  if (Math.abs(mins) < 60) {
    return `${mins}m`;
  } else {
    // This is similar to `staffMinutesToTime`, but here we want negative times, while in the
    // above we normalize it to positive only
    let hourStr = Math.trunc(mins / 60);
    let minsInHour = Math.abs(mins % 60);
    let minStr = minsInHour === 0 ? '' : ':' + staffTimePad(minsInHour);
    return `${hourStr}${minStr}h`;
  }
}

export function relativeTimeToMinutes(str: string): number {
  // First, remove either the 'm' or the 'h' characters
  str = str.replace(/mh/i, '');
  let colonIndex = str.indexOf(':');
  let result;

  if (colonIndex === -1) {
    result = parseInt(str, 10);
  } else {
    let fields = str.split(':');
    let hours = parseInt(fields[0], 10);
    let minutes = parseInt(fields[1], 10);
    result = hours * 60 + minutes;
  }

  if (Number.isNaN(result)) {
    throw new Error(`Invalid time ${str}`);
  }

  return result;
}

export function minutesToTimeSpan(minutes: number): string {
  let hours = Math.trunc(minutes / 60);
  let hourSuffix = hours > 1 ? 'hrs' : 'hr';
  if (minutes < 60) return numpad(minutes) + 'm';
  return `${hours}:${numpad(minutes % 60)}${hourSuffix}`;
}

export function isSameTime(time1?: Time, time2?: Time): boolean {
  if (!time1 || !time2) {
    // If both times are undefined, return true
    if (!time1 && !time2) return true;
    // If one of the times is undefined but not the other, return false
    return false;
  }
  return time1.hour === time2.hour && time1.minute === time2.minute;
}

export function stringToTime(str: string): Time {
  if (!str) {
    throw new Error('Time should not be empty');
  }

  let timeSuffix = str.substring(str.length - 2).toLowerCase();
  let _12hFormat = timeSuffix === 'am' || timeSuffix === 'pm';

  // remove suffix from string if 12h format so that we can parse the numbers
  if (_12hFormat) {
    str = str.substring(0, str.length - 2);
  }

  let [hourString, minuteString] = str.split(':').map(s => s.trim());

  if (!hourString || hourString.length > 2) {
    throw new Error('Invalid hours ' + str);
  }

  // Need to cast because the destructuring above does not account for
  // minuteString being undefined
  if ((minuteString as any) === undefined) {
    // Special case where the minutes are not specified
    minuteString = '00';
  } else if (minuteString.length !== 2) {
    throw new Error('Invalid minutes ' + str);
  }

  let hour = parseInt(hourString, 10);
  let minute = parseInt(minuteString, 10);

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    throw new Error('Invalid time ' + str);
  }

  // Convert to 24h format
  if (
    _12hFormat &&
    ((timeSuffix === 'pm' && hour !== 12) || (timeSuffix === 'am' && hour === 12))
  ) {
    hour = (hour + 12) % 24;
  }

  // Do some validations
  if (minute < 0 || minute > 59 || hour < 0 || hour > 24) {
    throw new Error('Invalid time ' + str);
  }

  return {
    hour,
    minute,
  };
}

export function stringToTimeIfValid(str: string): Time | undefined {
  try {
    const time = stringToTime(str);
    return time;
  } catch {
    return undefined;
  }
}

export const DEFAULT_BRAND_DATE_FORMAT: BrandDateFormat = {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  useOrdinals: false,
};

function getDaySuffix(day: number) {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

function timefmt(o: Time, format?: TimeStringFormat) {
  if (format === 'compact' || format === 'locale') {
    return o.hour + ':' + numpad(o.minute);
  } else if (format === 'xcompact') {
    return o.minute === 0 ? String(o.hour) : o.hour + ':' + numpad(o.minute);
  } else {
    return numpad(o.hour) + ':' + numpad(o.minute);
  }
}

function timeToString24(time: Time, format?: TimeStringFormat) {
  return timefmt(Object.assign({}, time), format);
}

function staffTimePad(n: number) {
  let negative = n < 0;
  if (negative) {
    n = Math.abs(n);
  }
  let padded = numpad(n);
  return (negative ? '-' : '') + padded;
}

export function isValidTime(u: unknown): u is Time {
  if ((u as any).hour === undefined || (u as any).minute === undefined) return false;

  let hour = (u as any).hour;
  let minute = (u as any).minute;

  if (typeof hour !== 'number' || typeof minute !== 'number') return false;

  let time = u as Time;
  if (time.hour < 0 || time.hour > 23 || time.minute < 0 || time.minute > 59) return false;

  return true;
}

export function normalizeMinutes(minutes: number): number {
  while (minutes < 0) {
    minutes += DAY_LENGTH;
  }
  return minutes;
}

export function formatTimeRange(time: Time, duration?: number): string {
  const start = timeToString(time, DEFAULT_HOUR_FORMAT, DEFAULT_TIMESTR_FORMAT);
  if (!duration) return start;
  const end = timeToString(
    addMinutesToTime(time, duration),
    DEFAULT_HOUR_FORMAT,
    DEFAULT_TIMESTR_FORMAT
  );
  return `${start} - ${end}`;
}

export function formatTimeRangeWithDayspan(
  time: Time,
  sublabel?: string,
  duration?: number,
  dayspan?: number
): string {
  const timeRange = sublabel ? sublabel : formatTimeRange(time, duration);
  return `${timeRange}${dayspan && dayspan > 1 ? ` (${dayspan} days)` : ''}`;
}

export function addLabelToTimeRange(timeRange: string, label?: string): string {
  return label ? `${label} (${timeRange})` : timeRange;
}

export function dateToTimeLocal(date?: Date): Time {
  const d = new Date(date || new Date());
  return { hour: d.getHours(), minute: d.getMinutes() };
}

export const WEEK_DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const MONTH_NAMES_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export function copyDate(date: Date): Date {
  return new Date(date);
}

/**
 * @deprecated DO NOT USE! THIS IS DANGEROUS BECAUSE THE STRING CAN HAVE ANY FORMAT
 * FOR EXAMPLE IT CAN BE AN ISO STRING OR IT CAN BE A STRING WITH A TIMEZONE IN IT
 * SO IT CAN LEAD TO DIFFERENT RESULTS IN DIFFERENT ENVIRONMENTS. USE WITH CAUTION.
 * For example, YYYY-MM-DDTHH:mm:ss.sssZ will be parsed as UTC, without the Z it will
 * be parsed as local time, and if its just YYYY-MM-DD it will be parsed as UTC.
 * IT'S A MESS - If its an ISO date, just use isoToDateUTC or isoToDateLocal
 * based on what you're working with. Or use the isoToDateOptional versions if you
 * want undefined as a possibility and not an exception.
 */
export function _dangerouslyParseUnknownStringToDate(str: string): Date | undefined {
  let date = new Date(str);
  if (Number.isNaN(date.getTime())) return;
  return date;
}

// ----- Working with ISO Dates (YYYY-MM-DD) ----- //

export function isIsoDate(iso: string) {
  const regexCheck = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  if (!regexCheck) return false;

  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  return (
    !Number.isNaN(date.getTime()) &&
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

export function isoToDateLocal(iso: string): Date {
  if (!isIsoDate(iso)) throw new Error(`Invalid date ${iso}`);
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date;
}

export function isoToDateUTC(iso: string): Date {
  if (!isIsoDate(iso)) throw new Error(`Invalid date ${iso}`);
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date;
}

export function isoToDateOptionalLocal(iso: string): Date | undefined {
  iso = iso.trim();
  if (!isIsoDate(iso)) return undefined;
  return isoToDateLocal(iso);
}

export function isoToDateOptionalUTC(iso: string): Date | undefined {
  iso = iso.trim();
  if (!isIsoDate(iso)) return undefined;
  return isoToDateUTC(iso);
}

export function dateToIsoLocal(date?: Date): string {
  const d = new Date(date || new Date());
  const timezoneOffset = d.getTimezoneOffset() * MS_PER_MINUTE;
  return new Date(d.getTime() - timezoneOffset).toISOString().substring(0, 10);
}

export function dateToIsoUTC(date?: Date): string {
  const d = new Date(date || new Date());
  return d.toISOString().substring(0, 10);
}

export function isoLocal(): string {
  // prefer getServerDateLocal().iso for getting the current date in ISO format on code that
  // sits on the server e.g. plugins index files. But not mandatory - its equivalent.
  return dateToIsoLocal(startOfDayLocal());
}

export function isoUTC(): string {
  return dateToIsoUTC(startOfDayUTC());
}

export function createIsoDateList(startIso: string, desiredLength: number): string[] {
  if (!isIsoDate(startIso) || desiredLength < 1) {
    throw new Error('Invalid start date or number of days');
  }

  const workingDay = isoToDateUTC(startIso);
  let dates: string[] = [startIso];

  for (let i = 1; i < desiredLength; i++) {
    dates.push(dateToIsoUTC(addDaysToDateUTC(workingDay, i)));
  }

  return dates;
}

export function addDaysToIsoDate(iso: string, days: number): string {
  return dateToIsoUTC(addDaysToDateUTC(isoToDateUTC(iso), days));
}

export function isIsoDateInRange(target: string, start: string, daysAhead: number): boolean {
  const targetDate = isoToDateUTC(target);
  const startDate = isoToDateUTC(start);

  const cutoffDate = addDaysToDateUTC(startDate, daysAhead);

  return targetDate >= startDate && targetDate <= cutoffDate;
}

export function daysBetweenIsoDates(startIso: string, endIso: string): number {
  const startDate = isoToDateUTC(startIso);
  const endDate = isoToDateUTC(endIso);

  const timeDiff = dateCompare(endDate, startDate);
  return Math.round(timeDiff / MS_PER_DAY);
}

// Convers YYYY-MM-DD to MM/DD/YYYY
export function isoToMonthDayYear(iso: string) {
  if (!isIsoDate(iso)) return undefined;
  const tokens = iso.split('-');
  if (tokens.length !== 3) return undefined;
  return `${tokens[1]}/${tokens[2]}/${tokens[0]}`;
}

// ----- Working with JS Dates ----- //

export function startOfDayLocal(date?: Date): Date {
  const d = new Date(date || new Date());
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function startOfDayUTC(date?: Date): Date {
  const d = new Date(date || new Date());
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

export function endOfDayLocal(date?: Date): Date {
  const d = new Date(date || new Date());
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

export function endOfDayUTC(date?: Date): Date {
  const d = new Date(date || new Date());
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999));
}

export function addDaysToDateLocal(date: Date, days: number): Date {
  let d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

export function addDaysToDateUTC(date: Date, days: number): Date {
  let d = new Date(date);
  d.setUTCDate(d.getUTCDate() + days);
  return d;
}

export function startOfMonthLocal(date?: Date): Date {
  const d = new Date(date || new Date());
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function endOfMonthLocal(date?: Date): Date {
  const d = new Date(date || new Date());
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

// resets day back to 1, then adds month offset, then adds nth day offset (defaults to 1)
// careful not to pass a large nthDay if you're not expecting overflow

// rename this one
export function navigateMonthsLocal(date: Date, monthOffset: number, nthDay?: number): Date {
  const firstDay = startOfMonthLocal(date);
  const newDate = new Date(firstDay.getFullYear(), firstDay.getMonth() + monthOffset, nthDay || 1);
  return newDate;
}

export function weekdayLocal(date?: Date): string {
  const d = new Date(date || new Date());
  return d.toLocaleString('en', { weekday: 'short' }).toUpperCase();
}

export function weekdayUTC(date?: Date): string {
  const d = new Date(date || new Date());
  return d.toLocaleString('en', { weekday: 'short', timeZone: 'UTC' }).toUpperCase();
}

export function isSameDayLocal(date1: Date, date2: Date): boolean {
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

export function isSameDayUTC(date1: Date, date2: Date): boolean {
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  return (
    d1.getUTCFullYear() === d2.getUTCFullYear() &&
    d1.getUTCMonth() === d2.getUTCMonth() &&
    d1.getUTCDate() === d2.getUTCDate()
  );
}

export function isDateUTC(date: Date): boolean {
  const d = new Date(date);

  return (
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0
  );
}

export function findMinMaxDate(...dates: Date[]) {
  let minDate: Date | undefined;
  let maxDate: Date | undefined;

  for (const date of dates) {
    const d = new Date(date);
    if (!minDate || minDate > d) minDate = d;
    if (!maxDate || maxDate < d) maxDate = d;
  }

  return [minDate, maxDate];
}

export function findMinMaxIso(...dateIso: string[]): [string, string] {
  const [a, b] = findMinMaxDate(...dateIso.map(isoToDateUTC));
  return [dateToIsoUTC(a), dateToIsoUTC(b)];
}

export function expandIso(iso: string, amt: number) {
  let start = isoToDateLocal(iso);
  let end = isoToDateLocal(iso);
  if (amt > 0) {
    start = addDaysToDateLocal(start, -amt);
    end = addDaysToDateLocal(end, amt);
  }
  return [dateToIsoLocal(start), dateToIsoLocal(end)];
}

// use this if you have a UTC date that you want to treat as if it was that time
// but in local time instead (e.g. parse the 'date' part as local, not UTC)
export function fromDateUTCToDateLocal(date: Date): Date {
  const iso = dateToIsoUTC(date);
  return isoToDateLocal(iso);
}

// use this if you have a local date that you want to treat as if it was that time
// but in UTC instead (e.g. parse the 'date' part as UTC, not local)
export function fromDateLocalToDateUTC(date: Date): Date {
  const iso = dateToIsoLocal(date);
  return isoToDateUTC(iso);
}

export function addTimeToDateUTC(date: Date, time: Time): Date {
  const d = new Date(date);
  d.setUTCHours(time.hour, time.minute, 0, 0);
  return d;
}

export function addTimeToDateLocal(date: Date, time: Time): Date {
  const d = new Date(date);
  d.setHours(time.hour, time.minute, 0, 0);
  return d;
}

export function dateCompare(date1: Date, date2: Date): number {
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  return d1.getTime() - d2.getTime();
}

export function getLocaleDateStringFromIso(
  iso: string,
  options?: Omit<Intl.DateTimeFormatOptions, 'timeZone'>,
  locale?: string | string[]
) {
  const date = isoToDateUTC(iso);
  return getLocaleDateStringUTC(date, options, locale);
}

export function getLocaleDateTimeStringFromIso(
  iso: string,
  options?: Omit<Intl.DateTimeFormatOptions, 'timeZone'>,
  locale?: string | string[]
) {
  const date = isoToDateUTC(iso);
  return getLocaleDateTimeStringUTC(date, options, locale);
}

export function getLocaleDateStringLocal(
  date?: Date,
  options?: Omit<Intl.DateTimeFormatOptions, 'timeZone'>,
  locale?: string | string[]
) {
  const d = new Date(date || new Date());
  return d.toLocaleDateString(locale, { ...options, timeZone: undefined });
}

export function getLocaleDateStringUTC(
  date?: Date,
  options?: Omit<Intl.DateTimeFormatOptions, 'timeZone'>,
  locale?: string | string[]
) {
  const d = new Date(date || new Date());
  return d.toLocaleDateString(locale, { ...options, timeZone: 'UTC' });
}

export function getLocaleDateTimeStringLocal(
  date?: Date,
  options?: Omit<Intl.DateTimeFormatOptions, 'timeZone'>,
  locale?: string | string[]
) {
  const d = new Date(date || new Date());
  return d.toLocaleString(locale, { ...options, timeZone: undefined });
}

export function getLocaleDateTimeStringUTC(
  date?: Date,
  options?: Omit<Intl.DateTimeFormatOptions, 'timeZone'>,
  locale?: string | string[]
) {
  const d = new Date(date || new Date());
  return d.toLocaleString(locale, { ...options, timeZone: 'UTC' });
}

export function timeToAgoLocal(dateSince: Date, currentDate: Date) {
  const ms = Math.floor(new Date(currentDate).getTime() - new Date(dateSince).getTime());

  const checkPlurality = (time: number) => (time > 1 ? 's' : '');
  if (ms < 0) return '';
  else if (ms < MS_PER_MINUTE) {
    const time = Math.round(ms / 1000);
    return `${time} second${checkPlurality(time)} ago`;
  } else if (ms < MS_PER_HOUR) {
    const time = Math.round(ms / MS_PER_MINUTE);
    return `${time} minute${checkPlurality(time)} ago`;
  } else if (ms < MS_PER_DAY) {
    const time = Math.round(ms / MS_PER_HOUR);
    return `${time} hour${checkPlurality(time)} ago`;
  } else if (ms < MS_PER_MONTH) {
    const time = Math.round(ms / MS_PER_DAY);
    return `${time} day${checkPlurality(time)} ago`;
  } else if (ms < MS_PER_YEAR) {
    const time = Math.round(ms / MS_PER_MONTH);
    return `${time} month${checkPlurality(time)} ago`;
  } else return 'long ago';
}

// -- Old date to string formats, we should migrate to using the ones above instead -- //

/** @deprecated */
export function dateToStringLongLocal(date: Date): string {
  const d = new Date(date);
  return `${MONTH_NAMES_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** @deprecated */
export function dateToStringLongUTC(date: Date): string {
  const d = new Date(date);
  return `${MONTH_NAMES_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

/** @deprecated */
export function dateTimeToStringLongLocal(date: Date): string {
  const d = new Date(date);
  return (
    `${MONTH_NAMES_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ` +
    `${numpad(d.getHours())}:${numpad(d.getMinutes())}:${numpad(d.getSeconds())}`
  );
}

/** @deprecated */
export function dateTimeToStringLongUTC(date: Date): string {
  const d = new Date(date);
  return (
    `${MONTH_NAMES_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()} ` +
    `${numpad(d.getUTCHours())}:${numpad(d.getUTCMinutes())}:${numpad(d.getUTCSeconds())}`
  );
}

/** @deprecated */
export function dateToStringLongerLocal(date: Date): string {
  const d = new Date(date);
  return `${WEEK_DAYS[d.getDay()]}, ${
    MONTH_NAMES_SHORT[d.getMonth()]
  } ${d.getDate()}, ${d.getFullYear()}`;
}

/** @deprecated */
export function dateToStringLongerNoYearLocal(date: Date): string {
  const d = new Date(date);
  return `${WEEK_DAYS[d.getDay()]}, ${MONTH_NAMES_SHORT[d.getMonth()]} ${d.getDate()}`;
}

/** @deprecated */
export function formatDateUTC(
  date: Date | undefined,
  locale: string,
  dateFormat: BrandDateFormat
): string {
  const d = new Date(date || new Date());

  const weekday = dateFormat.weekday;
  const month = dateFormat.month;
  const year = dateFormat.year;
  const day = dateFormat.day;

  // ordinals only for en-US
  if (dateFormat.useOrdinals && locale.startsWith('en')) {
    const ordinalWeekday = d.toLocaleString(locale, { weekday, timeZone: 'UTC' });
    const ordinalMonth = d.toLocaleString(locale, { month, timeZone: 'UTC' });
    const ordinalYear = d.toLocaleString(locale, { year, timeZone: 'UTC' });
    const ordinalDay = d.getUTCDate();

    const daySuffix = `${ordinalDay}${getDaySuffix(ordinalDay)}`;

    return `${ordinalWeekday}, ${daySuffix} ${ordinalMonth}, ${ordinalYear}`;
  }

  return d.toLocaleString(locale, {
    weekday,
    month,
    year,
    day,
    timeZone: 'UTC',
  });
}

/** @deprecated */
export function formatDateToMonthDayYearUTC(date?: Date): string {
  const d = new Date(date || new Date());

  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC',
  });
}

// ----- DateGroup abstraction ----- //

export interface DateGroup {
  iso: string;
  hours: number;
  minutes: number;
  _dangerousDate: Date;
}

export function getServerDateLocal(): DateGroup {
  const date = new Date();
  const currentHour = date.getHours();
  const currentMinute = date.getMinutes();

  const minutes = currentHour * 60 + currentMinute;
  const iso = dateToIsoLocal(date);

  return { iso, hours: Math.floor(minutes / 60), minutes, _dangerousDate: date };
}

export function getGODateLocal(dayStartMinutes: number, serverDate?: DateGroup): DateGroup {
  let { _dangerousDate: date, iso, minutes } = serverDate || getServerDateLocal();

  date = new Date(date);

  if (minutes < dayStartMinutes) {
    date = addDaysToDateLocal(date, -1);
    iso = dateToIsoLocal(date);
    minutes += 24 * 60;
  }

  return { iso, hours: Math.floor(minutes / 60), minutes, _dangerousDate: date };
}

export function addDaysToDateGroup(goDate: DateGroup, offsetDays: number): DateGroup {
  let newDate = new Date(goDate._dangerousDate);

  newDate = addDaysToDateLocal(newDate, offsetDays);

  return { ...goDate, iso: dateToIsoLocal(newDate), _dangerousDate: newDate };
}
